/**
 * Browser-side helpers shared by the stored-file upload flows (Work Request
 * attachments and Procedure photos): type checks, the SHA-256 checksum the
 * upload session is bound to, and the direct PUT to the presigned S3 URL.
 */

/** A stalled upload must fail so the caller is never left waiting on S3. */
export const ATTACHMENT_UPLOAD_TIMEOUT_MS = 120_000;

export const IMAGE_UPLOAD_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export type ImageUploadType = (typeof IMAGE_UPLOAD_TYPES)[number];

/** Matches the server's 25 MB limit for image attachments. */
export const MAX_IMAGE_UPLOAD_BYTES = 25 * 1024 * 1024;

function isImageUploadType(type: string): type is ImageUploadType {
  return (IMAGE_UPLOAD_TYPES as readonly string[]).includes(type);
}

/**
 * The image content type for a file, falling back to its extension when the
 * browser leaves `type` empty. Returns null for anything that is not a
 * supported image.
 */
export function imageContentType(file: File): ImageUploadType | null {
  if (isImageUploadType(file.type)) return file.type;
  if (file.type !== "") return null;
  switch (file.name.split(".").pop()?.toLowerCase()) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    default:
      return null;
  }
}

/** True for a non-empty JPEG, PNG or WebP image up to the size limit. */
export function isAcceptedImage(file: File) {
  return (
    imageContentType(file) !== null &&
    file.size > 0 &&
    file.size <= MAX_IMAGE_UPLOAD_BYTES
  );
}

/** Base64 SHA-256 of the file bytes, as S3's checksum header expects. */
export async function sha256Base64(file: File) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)));
}

/**
 * A direct upload to S3 that failed. `status` is the HTTP status S3 returned,
 * or null when the request never completed (network failure or timeout).
 */
export class StoredFileUploadError extends Error {
  constructor(
    message: string,
    readonly status: number | null = null,
  ) {
    super(message);
    this.name = "StoredFileUploadError";
  }
}

/** Uploads the bytes to the presigned URL; fails rather than hang on S3. */
export async function putStoredFileBytes(
  uploadUrl: string,
  file: File,
  checksumSha256: string,
  contentType: string,
) {
  let response: Response;
  try {
    response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
        "x-amz-checksum-sha256": checksumSha256,
      },
      body: file,
      signal: AbortSignal.timeout(ATTACHMENT_UPLOAD_TIMEOUT_MS),
    });
  } catch {
    throw new StoredFileUploadError(`Unable to upload ${file.name}.`);
  }
  if (!response.ok)
    throw new StoredFileUploadError(
      `Unable to upload ${file.name} (${response.status}).`,
      response.status,
    );
}
